import { Fragment, useEffect, useRef, useState } from "react";
import { UploadCloud, Eye, Loader2, Landmark, AlertTriangle, Pencil, Link2, ChevronDown, ChevronUp, Play, GripVertical, X } from "lucide-react";
import { T } from "../../ui/tokens";
import { fmt, fmtDate, pct } from "../../lib/format";
import { ACCEPT_EXTRATO, mensagemErroImportacao } from "../../lib/extratoFormatos";
import { apiFetch } from "../../lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { useExtratoCompleto, useBancosDisponiveis, useContasDuplicadas, usePeriodosExtrato, useMarcarResolvido, useConfirmMatchExtrato, useCorrigirMatch, useReordenarExtrato, extratoKey, bancosKey, periodosExtratoKey } from "./useExtrato";
import PainelConciliacao from "./PainelConciliacao";
import ModalCorrigirFatura from "./ModalCorrigirFatura";
import ModalEditarConta from "./ModalEditarConta";
import PainelCompararContas from "./PainelCompararContas";
import { precisaDecisao } from "./matching";
import type { MovimentoExtrato, BancoConta, ParContasDuplicadas } from "./types";

interface Empresa {
  nif: string;
  nome: string;
}
interface PreviewExtrato {
  ficheiro?: string;
  empresa_detetada?: Empresa | null;
  empresa_selecionada?: Empresa | null;
  nif_detetado?: string | null;
  empresas: Empresa[];
  banco?: string | null;
  conta?: string | null;
  periodo_inicio?: string | null;
  periodo_fim?: string | null;
  total_movimentos: number;
  movimentos: Array<{ data?: string; descricao?: string; valor: number; saldo?: number | null }>;
  validacao_saldo?: { ok: boolean; diferenca: number } | null;
}
interface Props {
  empresa?: string;
}

function estadoLinha(m: MovimentoExtrato): { label: string; cor: string; dot: string } {
  if (m.resolvido_sem_fatura) return { label: "Resolvida sem fatura", cor: T.textMid, dot: T.textLight };
  if (m.match_estado === "confirmado") return { label: "Casada", cor: T.green, dot: T.green };
  if (m.match_estado === "pendente") return { label: `Sugestão ${pct(m.match_confianca ?? 0)}`, cor: T.amber, dot: T.amber };
  if (m.tipo === "debito") return { label: "Sem fatura", cor: T.red, dot: T.red };
  return { label: "Sem fatura associada", cor: T.textLight, dot: T.textLight };
}

// Upload de extrato (CSV/Excel/PDF) — tenta identificar o banco e a conta
// sozinho a partir do próprio ficheiro; só pede para o utilizador indicar
// quando a deteção automática falha (uma empresa pode ter contas em bancos
// diferentes).
export function UploadExtrato({ empresaAtual, onImportado }: { empresaAtual?: string; onImportado: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const [avisoSaldo, setAvisoSaldo] = useState<string | null>(null);
  const [opcoes, setOpcoes] = useState<Empresa[] | null>(null);
  const [preview, setPreview] = useState<PreviewExtrato | null>(null);
  const [empresaPesquisa, setEmpresaPesquisa] = useState("");
  const [erroPreview, setErroPreview] = useState<string | null>(null);
  const [filePendente, setFilePendente] = useState<File | null>(null);
  const [empresaEscolha, setEmpresaEscolha] = useState("");
  const [bancoInput, setBancoInput] = useState("");
  const [contaInput, setContaInput] = useState("");
  const enviar = async (file: File, empresaNif?: string, bancoManual?: string, contaManual?: string, confirmar = false) => {
    setUploading(true);
    setMsg(null);
    setErroPreview(null);
    try {
      const fd = new FormData();
      fd.append("arquivo", file);
      if (empresaNif) fd.append("empresa_nif", empresaNif);
      if (bancoManual?.trim()) fd.append("banco", bancoManual.trim());
      if (contaManual?.trim()) fd.append("conta", contaManual.trim());
      if (confirmar) {
        fd.append("confirmar_empresa", "true");
        fd.append("confirmar_importacao", "true");
      } else {
        fd.append("previsualizar", "true");
      }
      const r = await apiFetch("/api/extratos/importar-arquivo", { method: "POST", body: fd });
      if (r.ok) {
        const d = await r.json();
        if (d.previsualizacao) {
          const empresasPreview = d.empresas || [];
          const nifInicial = d.empresa_detetada?.nif || d.empresa_selecionada?.nif || empresaAtual || "";
          setPreview(d);
          setFilePendente(file);
          setOpcoes(empresasPreview);
          setEmpresaEscolha(nifInicial);
          setEmpresaPesquisa("");
          setBancoInput(d.banco || "");
          setContaInput(d.conta || "");
          setMsg(null);
          return;
        }
        setPreview(null);
        setOpcoes(null);
        // Nunca bloqueia a importação -- só avisa quando o ficheiro trazia
        // saldo inicial/final (ou saldo corrido por movimento) e a soma dos
        // movimentos não fecha, sinal de que pode faltar algum movimento.
        setAvisoSaldo(
          d.validacao_saldo && !d.validacao_saldo.ok
            ? `Saldo inicial + movimentos não bate certo com o saldo final (diferença de ${Math.abs(d.validacao_saldo.diferenca).toFixed(2)}€) — confirma o ficheiro original.`
            : null
        );
        const resumo = d.inseridos > 0
          ? `${d.inseridos} movimento(s) novo(s) importado(s)${d.novos_matches > 0 ? ` · ${d.novos_matches} sugestão/ões de match` : ""}.`
          : `Extrato já importado — ${d.ja_existiam || 0} movimento(s) já existiam.`;
        setFilePendente(null);
        setBancoInput("");
        setContaInput("");
        setMsg({ ok: true, texto: resumo });
        onImportado();
      } else {
        const d = await r.json().catch(() => ({}));
        const texto = mensagemErroImportacao(d?.detail);
        if (preview) setErroPreview(texto);
        else setMsg({ ok: false, texto });
        setAvisoSaldo(null);
      }
    } catch {
      if (preview) setErroPreview("Erro de rede — tenta de novo.");
      else setMsg({ ok: false, texto: "Erro de rede — tenta de novo." });
      setAvisoSaldo(null);
    } finally {
      setUploading(false);
    }
  };

  const empresasFiltradas = (opcoes || []).filter((empresa) => {
    const pesquisa = empresaPesquisa.trim().toLowerCase();
    return !pesquisa || empresa.nome.toLowerCase().includes(pesquisa) || empresa.nif.includes(pesquisa);
  });

  return (
    <div style={{ marginBottom: 20 }}>
      <style>{"@keyframes spin-extrato { to { transform: rotate(360deg); } }"}</style>
      <div
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) enviar(f, empresaAtual); }}
        style={{
          border: `2px dashed ${dragOver ? T.blue : T.border}`,
          borderRadius: 12, padding: "20px", textAlign: "center",
          cursor: uploading ? "default" : "pointer",
          background: dragOver ? T.blueBg : T.surface,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        }}
      >
        <input ref={fileRef} type="file" accept={ACCEPT_EXTRATO} style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) enviar(f, empresaAtual); }} />
        {uploading
          ? <Loader2 size={18} color={T.blue} style={{ animation: "spin-extrato 0.8s linear infinite" }} />
          : <UploadCloud size={18} color={T.textMid} />}
        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
          {uploading ? "A processar extrato…" : "Arrasta o extrato bancário ou clica para escolher"}
        </span>
      </div>
      {msg && (
        <div style={{ marginTop: 8, padding: "9px 14px", borderRadius: 8, fontSize: 12.5, background: msg.ok ? T.greenBg : T.redBg, color: msg.ok ? T.green : T.red, border: `1px solid ${msg.ok ? T.greenBorder : T.redBorder}` }}>
          {msg.ok ? "✓ " : "✗ "}{msg.texto}
        </div>
      )}
      {avisoSaldo && (
        <div style={{ marginTop: 8, padding: "9px 14px", borderRadius: 8, fontSize: 12.5, background: T.amberBg, color: T.amber, border: `1px solid ${T.amberBorder}` }}>
          ⚠ {avisoSaldo}
        </div>
      )}
      {preview && filePendente && (
        <div style={{ position: "fixed", inset: 0, zIndex: 3800, background: "rgba(15,23,42,.62)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ width: "min(940px,96vw)", maxHeight: "92vh", background: T.surface, borderRadius: 16, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,.28)" }}>
            <div style={{ padding: "17px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Confirmar importação do extrato</div>
                <div style={{ fontSize: 12, color: T.textMid, marginTop: 3 }}>{preview.ficheiro || filePendente.name} · nenhuma linha foi importada ainda</div>
              </div>
              <button type="button" disabled={uploading} onClick={() => { setPreview(null); setFilePendente(null); setOpcoes(null); }} style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${T.border}`, background: T.bg, color: T.textMid, cursor: "pointer" }}><X size={15} /></button>
            </div>

            <div style={{ padding: 20, overflowY: "auto", display: "grid", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(280px,1.25fr) minmax(260px,1fr)", gap: 14 }}>
                <div style={{ padding: 14, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: T.text, marginBottom: 8 }}>Empresa que receberá o extrato</div>
                  {preview.empresa_detetada && <div style={{ padding: "8px 10px", marginBottom: 9, borderRadius: 7, background: T.greenBg, color: T.green, fontSize: 12 }}>
                    Detetada no ficheiro: <strong>{preview.empresa_detetada.nome}</strong> · {preview.empresa_detetada.nif}
                  </div>}
                  {preview.nif_detetado && !preview.empresa_detetada && <div style={{ padding: "8px 10px", marginBottom: 9, borderRadius: 7, background: T.amberBg, color: T.amber, fontSize: 12 }}>
                    NIPC encontrado no ficheiro: {preview.nif_detetado}, mas não corresponde a uma empresa ativa.
                  </div>}
                  <input value={empresaPesquisa} onChange={(e) => setEmpresaPesquisa(e.target.value)} placeholder="Pesquisar por nome ou NIPC…" style={{ width: "100%", padding: "8px 10px", boxSizing: "border-box", borderRadius: 7, border: `1px solid ${T.border}`, fontSize: 12.5, outline: "none" }} />
                  <div style={{ marginTop: 7, maxHeight: 145, overflowY: "auto", display: "grid", gap: 5 }}>
                    {empresasFiltradas.map((empresa) => <label key={empresa.nif} style={{ padding: "7px 9px", border: `1px solid ${empresaEscolha === empresa.nif ? T.blueBorder : T.border}`, borderRadius: 7, background: empresaEscolha === empresa.nif ? T.blueBg : T.surface, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                      <input type="radio" name="empresa-extrato" checked={empresaEscolha === empresa.nif} onChange={() => setEmpresaEscolha(empresa.nif)} />
                      <span style={{ fontSize: 12.5, color: T.text }}><strong>{empresa.nome}</strong> · {empresa.nif}</span>
                    </label>)}
                    {!empresasFiltradas.length && <div style={{ fontSize: 12, color: T.textLight, padding: 8 }}>Nenhuma empresa encontrada.</div>}
                  </div>
                </div>

                <div style={{ padding: 14, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: T.text, marginBottom: 8 }}>Conta e período</div>
                  <input value={bancoInput} onChange={(e) => setBancoInput(e.target.value)} placeholder="Banco" style={{ width: "100%", padding: "8px 10px", boxSizing: "border-box", borderRadius: 7, border: `1px solid ${T.border}`, fontSize: 12.5, marginBottom: 7 }} />
                  <input value={contaInput} onChange={(e) => setContaInput(e.target.value)} placeholder="Número da conta" style={{ width: "100%", padding: "8px 10px", boxSizing: "border-box", borderRadius: 7, border: `1px solid ${T.border}`, fontSize: 12.5 }} />
                  <div style={{ fontSize: 12, color: T.textMid, marginTop: 10 }}>
                    Período: <strong>{preview.periodo_inicio ? fmtDate(preview.periodo_inicio) : "—"} — {preview.periodo_fim ? fmtDate(preview.periodo_fim) : "—"}</strong>
                  </div>
                  <div style={{ fontSize: 12, color: T.textMid, marginTop: 5 }}>Movimentos: <strong>{preview.total_movimentos}</strong></div>
                  {preview.validacao_saldo && <div style={{ marginTop: 9, padding: "8px 10px", borderRadius: 7, fontSize: 11.5, background: preview.validacao_saldo.ok ? T.greenBg : T.amberBg, color: preview.validacao_saldo.ok ? T.green : T.amber }}>
                    {preview.validacao_saldo.ok ? "Os saldos do extrato fecham corretamente." : `Os saldos apresentam uma diferença de ${fmt(Math.abs(preview.validacao_saldo.diferenca))}.`}
                  </div>}
                </div>
              </div>

              <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                <div style={{ padding: "10px 12px", background: T.bg, fontSize: 11, fontWeight: 800, color: T.text }}>Linhas que serão importadas</div>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead><tr style={{ color: T.textLight, textAlign: "left" }}><th style={{ padding: "8px 10px" }}>Data</th><th style={{ padding: "8px 10px" }}>Descrição</th><th style={{ padding: "8px 10px", textAlign: "right" }}>Valor</th><th style={{ padding: "8px 10px", textAlign: "right" }}>Saldo</th></tr></thead>
                    <tbody>{preview.movimentos.map((movimento, indice) => <tr key={`${movimento.data}-${movimento.valor}-${indice}`} style={{ borderTop: `1px solid ${T.border}` }}>
                      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{movimento.data ? fmtDate(movimento.data) : "—"}</td>
                      <td style={{ padding: "8px 10px", color: T.text }}>{movimento.descricao || "—"}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: movimento.valor < 0 ? T.red : T.green }}>{fmt(movimento.valor)}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: T.textMid }}>{movimento.saldo == null ? "—" : fmt(movimento.saldo)}</td>
                    </tr>)}</tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={{ padding: "13px 20px", borderTop: `1px solid ${T.border}`, background: T.bg, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 11.5, color: erroPreview ? T.red : T.textMid }}>{erroPreview || "Só depois de confirmares serão criados movimentos, conta, sugestões e arquivo no Drive."}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" disabled={uploading} onClick={() => { setPreview(null); setFilePendente(null); setOpcoes(null); }} style={{ padding: "8px 14px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface, color: T.textMid, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
                <button type="button" disabled={uploading || !empresaEscolha || !bancoInput.trim() || !contaInput.trim()} onClick={() => enviar(filePendente, empresaEscolha, bancoInput, contaInput, true)} style={{ padding: "8px 15px", borderRadius: 7, border: "none", background: T.blue, color: "#fff", fontWeight: 800, cursor: "pointer", opacity: uploading || !empresaEscolha || !bancoInput.trim() || !contaInput.trim() ? .5 : 1 }}>
                  {uploading ? "A importar…" : `Confirmar e importar ${preview.total_movimentos} movimentos`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExtratoBancarioTab({ empresa }: Props) {
  const [periodo, setPeriodo] = useState<string>("");
  const [banco, setBanco] = useState<string>("");
  const [conta, setConta] = useState<string>("");
  const empresaAnterior = useRef(empresa);
  // Painel guiado: guarda só o ID de entrada — o painel constrói a sua
  // própria fila e relê cada movimento da lista viva (ver PainelConciliacao).
  const [painelIdInicial, setPainelIdInicial] = useState<number | null>(null);
  const [corrigirMovimento, setCorrigirMovimento] = useState<MovimentoExtrato | null>(null);
  // Cartão cuja conta está a ser editada, e par de contas em comparação.
  const [contaAEditar, setContaAEditar] = useState<BancoConta | null>(null);
  const [parAComparar, setParAComparar] = useState<ParContasDuplicadas | null>(null);
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const toggleExpandido = (id: number) => setExpandidos((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const qc = useQueryClient();
  const { data: movimentos, isLoading, isError } = useExtratoCompleto(empresa, periodo || undefined, banco || undefined, conta || undefined);
  const { data: bancos } = useBancosDisponiveis(empresa, periodo || undefined);
  const { data: mesesRecebidos = [], isLoading: mesesLoading } = usePeriodosExtrato(empresa);
  // Defesa adicional: mesmo que uma versão antiga da API devolva dados
  // corrompidos, o utilizador nunca vê meses impossíveis como 2026-15.
  const meses = (mesesRecebidos || []).filter((valor: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(valor));
  const nomePeriodo = (valor: string) => {
    const [ano, mes] = valor.split("-").map(Number);
    const nome = ["", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"][mes];
    return nome ? `${nome} ${ano}` : valor;
  };
  // Sem filtro de período: um duplicado é uma propriedade das contas, não do
  // mês que se está a ver. Filtrá-lo por mês escondia o aviso sempre que se
  // navegasse para um mês sem movimentos.
  const { data: duplicados } = useContasDuplicadas(empresa);
  const marcarResolvido = useMarcarResolvido(empresa, periodo || undefined, banco || undefined, conta || undefined);
  const confirmMatch = useConfirmMatchExtrato(empresa, periodo || undefined, banco || undefined, conta || undefined);
  const corrigirMatch = useCorrigirMatch(empresa, periodo || undefined, banco || undefined, conta || undefined);
  const reordenar = useReordenarExtrato(empresa, periodo || undefined, banco || undefined, conta || undefined);
  const [arrastandoId, setArrastandoId] = useState<number | null>(null);
  const [alvoId, setAlvoId] = useState<number | null>(null);

  // Reordenar só é possível sem filtro de banco/conta: o backend exige a
  // ordem do DIA INTEIRO de uma vez (senão a ordenação fica ambígua -- ver
  // POST /api/extratos/reordenar), e com um filtro ativo a lista pode estar
  // a esconder movimentos desse mesmo dia noutra conta.
  const podeReordenar = !banco && !conta;

  const largarSobre = (destinoId: number) => {
    const origemId = arrastandoId;
    setArrastandoId(null);
    setAlvoId(null);
    if (!origemId || origemId === destinoId || !movimentos) return;
    const origem = movimentos.find((m: MovimentoExtrato) => m.id === origemId);
    const destino = movimentos.find((m: MovimentoExtrato) => m.id === destinoId);
    if (!origem || !destino || origem.data_mov !== destino.data_mov) return;

    const idsDoDia = movimentos.filter((m: MovimentoExtrato) => m.data_mov === origem.data_mov).map((m: MovimentoExtrato) => m.id);
    const semOrigem = idsDoDia.filter((id: number) => id !== origemId);
    const posDestino = semOrigem.indexOf(destinoId);
    if (posDestino < 0) return;
    semOrigem.splice(posDestino, 0, origemId);
    reordenar.mutate({ ids: semOrigem });
  };
  const sel: React.CSSProperties = { padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 13, cursor: "pointer", outline: "none" };

  useEffect(() => {
    setBanco("");
    setConta("");
    setContaAEditar(null);
    setParAComparar(null);
  }, [empresa]);

  useEffect(() => {
    const mudouEmpresa = empresaAnterior.current !== empresa;
    empresaAnterior.current = empresa;
    setPeriodo((atual) => {
      if (!meses.length) return "";
      if (mudouEmpresa || !atual || !meses.includes(atual)) return meses[0];
      return atual;
    });
  }, [empresa, meses]);

  const onImportado = () => {
    qc.invalidateQueries({ queryKey: periodosExtratoKey(empresa) });
    qc.invalidateQueries({ queryKey: extratoKey(empresa, periodo || undefined, banco || undefined, conta || undefined) });
    qc.invalidateQueries({ queryKey: bancosKey(empresa, periodo || undefined) });
  };

  const darComoPaga = (m: MovimentoExtrato) => {
    const nota = window.prompt("Nota (opcional) — porque é que este movimento nunca vai ter fatura correspondente:", "") || undefined;
    marcarResolvido.mutate({ id: m.id, nota, resolver: true });
  };
  const reativar = (m: MovimentoExtrato) => marcarResolvido.mutate({ id: m.id, resolver: false });

  // O painel NÃO fecha ao concluir uma ação — fica aberto e o utilizador
  // avança para o movimento seguinte da fila. É esse o ponto do fluxo
  // guiado: despachar o mês de uma sentada, sem voltar à tabela a cada passo.
  const abrirPainel = (m: MovimentoExtrato) => {
    confirmMatch.reset();
    corrigirMatch.reset();
    setPainelIdInicial(m.id);
  };
  const abrirCorrigir = (m: MovimentoExtrato) => { corrigirMatch.reset(); setCorrigirMovimento(m); };

  const associarFatura = (faturaIds: number[]) => {
    if (!corrigirMovimento) return;
    corrigirMatch.mutate(
      { extratoId: corrigirMovimento.id, faturaIds, matchIdAntigo: corrigirMovimento.match_id ?? null },
      { onSuccess: () => setTimeout(() => setCorrigirMovimento(null), 400) }
    );
  };

  const porResolver = (movimentos || []).filter(precisaDecisao);

  const contaAtiva = (bancos || []).find((b: BancoConta) => b.banco === banco && b.conta === conta);

  // conta_id -> o par de que faz parte, para marcar os DOIS cartões
  // envolvidos (uma conta pode aparecer em mais do que um par; fica o
  // primeiro, que é quanto basta para abrir a comparação).
  const paresPorConta = new Map<number, ParContasDuplicadas>();
  for (const par of duplicados || []) {
    if (!paresPorConta.has(par.conta_id_a)) paresPorConta.set(par.conta_id_a, par);
    if (!paresPorConta.has(par.conta_id_b)) paresPorConta.set(par.conta_id_b, par);
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>1. Escolhe a conta e o mês</div>
          <div style={{ fontSize: 12.5, color: T.textMid, marginTop: 3 }}>Só serão mostrados os movimentos da conta selecionada.</div>
        </div>
        {!!meses.length && <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: ".06em" }}>
          Mês a tratar
        <select style={sel} value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
          {meses.map((m: string) => <option key={m} value={m}>{nomePeriodo(m)}</option>)}
        </select>
        </label>}
      </div>

      {!empresa ? (
        <div style={{ padding: "64px 24px", textAlign: "center", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Seleciona uma empresa</div>
          <div style={{ fontSize: 13, color: T.textMid, marginTop: 4 }}>Escolhe a empresa para veres o extrato bancário.</div>
        </div>
      ) : (
        <>
          <UploadExtrato empresaAtual={empresa} onImportado={onImportado} />

          {mesesLoading ? (
            <div style={{ padding: 32, textAlign: "center", color: T.textLight }}>A carregar períodos…</div>
          ) : !periodo ? (
            <div style={{ padding: "36px 20px", textAlign: "center", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, color: T.textMid, fontSize: 13 }}>
              Ainda não existem extratos importados para esta empresa.
            </div>
          ) : !!bancos?.length ? (
            <div className="contas-bancarias-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: 12, marginBottom: 18 }}>
              {bancos.map((s: BancoConta) => {
                const incompleta = !s.banco?.trim() || !s.conta?.trim();
                const contaSelecionada = banco === s.banco && conta === s.conta;
                const parDuplicado = paresPorConta.get(s.conta_id);
                const selecionar = () => { setBanco(s.banco || ""); setConta(s.conta || ""); };
                const nomeAcessivel = s.banco?.trim() || `conta ${s.conta || "por configurar"}`;
                return (
                  // Cartão como <div role="button"> e não <button>: leva lá
                  // dentro os botões de editar e comparar, e um <button>
                  // dentro de outro é HTML inválido (o browser fecha o
                  // primeiro e o clique deixa de fazer o que se espera).
                  // Teclado é tratado à mão para não perder a acessibilidade
                  // que o <button> dava de graça.
                  <div
                    key={s.conta_id}
                    role="button"
                    tabIndex={0}
                    aria-pressed={contaSelecionada}
                    onClick={selecionar}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selecionar(); }
                    }}
                    style={{ position: "relative", padding: 0, textAlign: "left", border: `2px solid ${contaSelecionada ? T.blue : parDuplicado ? T.amberBorder : incompleta ? T.amberBorder : T.border}`, borderRadius: 13, overflow: "hidden", cursor: "pointer", background: T.surface, boxShadow: contaSelecionada ? "0 0 0 3px rgba(3,54,61,.10)" : "0 2px 7px rgba(16,24,40,.04)" }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 11, padding: "15px 16px 12px" }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, display: "grid", placeItems: "center", flexShrink: 0, background: incompleta ? T.amberBg : T.blueBg, color: incompleta ? T.amber : T.blue }}>
                        {incompleta ? <AlertTriangle size={17} /> : <Landmark size={17} />}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        {/* paddingRight abre espaço para o botão de editar,
                            que fica em position:absolute no canto — sem isto
                            o nome do banco passava por baixo dele em mobile. */}
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, paddingRight: 26 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, color: incompleta ? T.amber : T.text }}>{s.banco?.trim() || "Conta por configurar"}</div>
                          {contaSelecionada && <span style={{ fontSize: 9.5, fontWeight: 800, color: T.blue, background: T.blueBg, borderRadius: 12, padding: "2px 7px", alignSelf: "flex-start", whiteSpace: "nowrap" }}>SELECIONADA</span>}
                        </div>
                        <div style={{ fontSize: 11.5, color: T.textMid, marginTop: 3, fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis" }}>{s.conta || "Número de conta em falta"}</div>
                      </div>
                    </div>

                    {/* Discreto e fora do fluxo, para não competir com o
                        indicador "SELECIONADA". stopPropagation impede que
                        editar selecione o cartão por baixo. */}
                    <button
                      type="button"
                      aria-label={`Editar conta ${nomeAcessivel}`}
                      title="Editar conta"
                      onClick={(e) => { e.stopPropagation(); setContaAEditar(s); }}
                      style={{ position: "absolute", top: 9, right: 9, display: "grid", placeItems: "center", width: 26, height: 26, borderRadius: 7, border: "none", background: "transparent", color: T.textLight, cursor: "pointer", lineHeight: 0 }}
                    >
                      <Pencil size={13.5} />
                    </button>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: `1px solid ${T.borderSoft}`, background: T.bg }}>
                      <div style={{ padding: "9px 16px", borderRight: `1px solid ${T.borderSoft}` }}>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: T.textLight, textTransform: "uppercase" }}>Neste mês</div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text, marginTop: 2 }}>{s.total_movimentos} movimentos · {s.total_extratos} extrato{s.total_extratos === 1 ? "" : "s"}</div>
                      </div>
                      <div style={{ padding: "9px 16px" }}>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: T.textLight, textTransform: "uppercase" }}>Período importado</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.textMid, marginTop: 2 }}>{s.periodo_inicio ? `${fmtDate(s.periodo_inicio)} — ${fmtDate(s.periodo_fim || s.periodo_inicio)}` : "Sem extrato neste mês"}</div>
                      </div>
                    </div>
                    {incompleta && <div style={{ padding: "8px 16px", background: T.amberBg, color: T.amber, fontSize: 11.5, fontWeight: 700 }}>Dados da conta incompletos — identifica o banco antes de conciliar.</div>}
                    {parDuplicado && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", padding: "8px 16px", background: T.amberBg, color: T.amber, fontSize: 11.5, fontWeight: 700, borderTop: `1px solid ${T.amberBorder}` }}>
                        <span>Possível conta duplicada</span>
                        {/* Sem "Fundir" aqui de propósito: a decisão só se
                            toma depois de ver a evidência lado a lado. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setParAComparar(parDuplicado); }}
                          style={{ padding: "4px 10px", borderRadius: 7, border: `1px solid ${T.amberBorder}`, background: T.surface, color: T.amber, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
                        >
                          Comparar contas
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: "18px", marginBottom: 18, borderRadius: 12, border: `1px solid ${T.border}`, background: T.surface, color: T.textMid, fontSize: 13 }}>Ainda não existem contas bancárias registadas para esta empresa.</div>
          )}

          {contaAtiva ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "11px 14px", marginBottom: 12, borderRadius: 10, border: `1px solid ${T.blueBorder}`, background: T.blueBg }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.blue, textTransform: "uppercase", letterSpacing: ".06em" }}>2. Extrato em tratamento</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginTop: 3 }}>{contaAtiva.banco || "Banco por configurar"} · {contaAtiva.conta || "conta em falta"} · {periodo}</div>
              </div>
              <div style={{ fontSize: 12, color: T.textMid }}>{contaAtiva.total_movimentos} movimentos encontrados</div>
            </div>
          ) : (
            <div style={{ padding: "13px 16px", marginBottom: 12, borderRadius: 10, border: `1px solid ${T.amberBorder}`, background: T.amberBg, color: T.amber, fontSize: 13, fontWeight: 700 }}>Seleciona acima a conta bancária que queres conciliar.</div>
          )}

          {/* Arranque do fluxo guiado — o caminho normal de trabalho: em vez
              de caçar linhas na tabela, percorre de uma vez todos os
              movimentos do mês que ainda precisam de decisão. */}
          {contaAtiva && !!movimentos?.length && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "12px 16px", marginBottom: 12, borderRadius: 10, border: `1px solid ${porResolver.length ? T.amberBorder : T.greenBorder}`, background: porResolver.length ? T.amberBg : T.greenBg }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: porResolver.length ? T.amber : T.green }}>
                {porResolver.length
                  ? `${porResolver.length} movimento(s) por conciliar${periodo ? ` em ${periodo}` : ""}.`
                  : `Tudo conciliado${periodo ? ` em ${periodo}` : ""}.`}
              </div>
              {porResolver.length > 0 && (
                <button
                  onClick={() => abrirPainel(porResolver[0])}
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 8, border: "none", background: T.blue, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
                >
                  <Play size={13} strokeWidth={2.5} /> Conciliar um a um
                </button>
              )}
            </div>
          )}

          {!contaAtiva ? null : isError ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: T.red, fontSize: 13 }}>Não foi possível carregar o extrato.</div>
          ) : isLoading || !movimentos ? (
            <div style={{ padding: "40px 24px", textAlign: "center", color: T.textLight, fontSize: 13 }}>A carregar…</div>
          ) : movimentos.length === 0 ? (
            <div style={{ padding: "48px 24px", textAlign: "center", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, color: T.textLight, fontSize: 13 }}>
              Sem movimentos importados{periodo ? " neste período" : ""}{banco ? ` em ${banco}` : ""}. Importa um extrato para começar.
            </div>
          ) : (
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                <thead>
                  <tr>
                    <th style={{ width: 28, background: T.bg, borderBottom: `1px solid ${T.border}` }} />
                    {["Data", "Descrição", "Banco", "Valor", "Saldo", "Estado", ""].map((h) => (
                      <th key={h} style={{ padding: "9px 14px", textAlign: h === "Valor" || h === "Saldo" ? "right" : "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textLight, background: T.bg, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movimentos.map((m: MovimentoExtrato) => {
                    const est = estadoLinha(m);
                    const faturas = m.faturas || [];
                    const temVarias = faturas.length > 1;
                    const aberto = expandidos.has(m.id);
                    return (
                      <Fragment key={m.id}>
                      <tr
                        onDragOver={(e) => {
                          if (!podeReordenar || arrastandoId == null) return;
                          const origem = movimentos.find((x: MovimentoExtrato) => x.id === arrastandoId);
                          if (!origem || origem.data_mov !== m.data_mov) return;  // só dentro do mesmo dia
                          e.preventDefault();
                          if (alvoId !== m.id) setAlvoId(m.id);
                        }}
                        onDragLeave={() => { if (alvoId === m.id) setAlvoId(null); }}
                        onDrop={(e) => { e.preventDefault(); largarSobre(m.id); }}
                        style={{
                          borderTop: alvoId === m.id ? `2px solid ${T.blue}` : `1px solid ${T.borderSoft}`,
                          opacity: arrastandoId === m.id ? 0.4 : 1,
                        }}
                      >
                        <td style={{ padding: 0, textAlign: "center", verticalAlign: "middle" }}>
                          {podeReordenar && (
                            <span
                              draggable
                              onDragStart={() => setArrastandoId(m.id)}
                              onDragEnd={() => { setArrastandoId(null); setAlvoId(null); }}
                              title="Arrasta para mudar a ordem deste movimento dentro do mesmo dia"
                              style={{ display: "inline-flex", cursor: "grab", color: T.textLight, padding: "0 2px" }}
                            >
                              <GripVertical size={13} strokeWidth={2} />
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12.5, color: T.textMid, whiteSpace: "nowrap" }}>{fmtDate(m.data_mov)}</td>
                        <td style={{ padding: "10px 14px", fontSize: 13, color: T.text, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.descricao}>
                          {m.descricao}
                          {!temVarias && m.numero_documento && (
                            <div style={{ fontSize: 11, color: T.textLight, marginTop: 1 }}>{m.nome_emitente} · {m.numero_documento}</div>
                          )}
                          {temVarias && (
                            <div
                              onClick={() => toggleExpandido(m.id)}
                              style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: T.blue, marginTop: 1, cursor: "pointer", fontWeight: 600 }}
                            >
                              {aberto ? <ChevronUp size={11} strokeWidth={2.5} /> : <ChevronDown size={11} strokeWidth={2.5} />}
                              {faturas.length} faturas associadas
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: T.textMid, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.conta || undefined}>
                          {m.banco || "—"}
                          {m.conta && <div style={{ fontSize: 10.5, color: T.textLight, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{m.conta}</div>}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontSize: 13.5, fontWeight: 700, color: m.valor < 0 ? T.red : T.green, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {fmt(m.valor)}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "right", fontSize: 12, color: T.textLight, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {m.saldo != null ? fmt(m.saldo) : "—"}
                        </td>
                        <td style={{ padding: "10px 14px", minWidth: m.resolvido_sem_fatura ? 190 : undefined }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: est.cor, fontWeight: 600 }}>
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: est.dot, flexShrink: 0 }} />
                            {est.label}
                          </span>
                          {!!m.resolvido_sem_fatura && m.resolvido_nota && (
                            <div
                              title={m.resolvido_nota}
                              style={{ marginTop: 3, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11, lineHeight: 1.3, color: T.textLight }}
                            >
                              Motivo: {m.resolvido_nota}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                          <div style={{ display: "inline-flex", gap: 6 }}>
                            {m.match_estado === "pendente" && m.match_id != null && (
                              <button
                                onClick={() => abrirPainel(m)}
                                title="Rever fatura e movimento antes de aceitar"
                                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.amberBorder}`, background: T.amberBg, color: T.amber, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                              >
                                <Eye size={12} strokeWidth={2.5} /> Rever
                              </button>
                            )}
                            {m.match_estado === "confirmado" && (
                              <button
                                onClick={() => abrirCorrigir(m)}
                                title="A associação está errada? Desfaz e escolhe outra fatura"
                                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.textMid, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                              >
                                <Pencil size={11} strokeWidth={2.5} /> Corrigir
                              </button>
                            )}
                            {!m.match_estado && !m.resolvido_sem_fatura && (
                              <button
                                onClick={() => abrirPainel(m)}
                                title="Abrir o painel para ver o extrato e escolher a fatura"
                                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.textMid, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                              >
                                <Link2 size={11} strokeWidth={2.5} /> Associar fatura
                              </button>
                            )}
                            {!m.match_estado && !m.resolvido_sem_fatura && m.tipo === "debito" && (
                              <button
                                disabled={marcarResolvido.isPending}
                                onClick={() => darComoPaga(m)}
                                title="Marcar como resolvido — nunca vai ter fatura correspondente"
                                style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.textMid, fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                              >
                                Dar como paga
                              </button>
                            )}
                            {!!m.resolvido_sem_fatura && (
                              <button
                                disabled={marcarResolvido.isPending}
                                onClick={() => reativar(m)}
                                title={m.resolvido_nota ? `Nota: ${m.resolvido_nota}` : "Reativar — volta a contar como pendente"}
                                style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "none", color: T.blue, fontSize: 11, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                              >
                                Reativar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {temVarias && aberto && (
                        <tr>
                          <td colSpan={8} style={{ padding: "0 14px 10px 34px", background: T.bg }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 6 }}>
                              {faturas.map((f: NonNullable<MovimentoExtrato["faturas"]>[number]) => (
                                <div key={f.match_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: T.textMid }}>
                                  <span>{f.nome_emitente || "—"} · {f.numero_documento || "—"}</span>
                                  <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: T.text }}>{fmt(f.valor ?? undefined)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {painelIdInicial != null && empresa && movimentos && (
        <PainelConciliacao
          movimentos={movimentos}
          idInicial={painelIdInicial}
          empresa={empresa}
          periodo={periodo || undefined}
          busy={confirmMatch.isPending || corrigirMatch.isPending || marcarResolvido.isPending}
          erro={
            confirmMatch.isError ? (confirmMatch.error as Error)?.message || "erro desconhecido"
            : corrigirMatch.isError ? (corrigirMatch.error as Error)?.message || "erro desconhecido"
            : null
          }
          onConfirmarSugestao={(matchId) => confirmMatch.mutate({ matchId, estado: "confirmado" })}
          onRejeitarSugestao={(matchId) => confirmMatch.mutate({ matchId, estado: "rejeitado" })}
          onAssociar={(mov, faturaIds) =>
            corrigirMatch.mutate({ extratoId: mov.id, faturaIds, matchIdAntigo: mov.match_id ?? null })
          }
          onResolverSemFatura={(mov) => {
            const nota = window.prompt("Nota (opcional) — porque é que este movimento nunca vai ter fatura:", "") || undefined;
            marcarResolvido.mutate({ id: mov.id, nota, resolver: true });
          }}
          onExtratoSubstituido={() => {
            setPainelIdInicial(null);
            onImportado();
          }}
          onClose={() => setPainelIdInicial(null)}
        />
      )}

      {corrigirMovimento && empresa && (
        <ModalCorrigirFatura
          movimento={corrigirMovimento}
          empresa={empresa}
          busy={corrigirMatch.isPending}
          erro={corrigirMatch.isError ? (corrigirMatch.error as Error)?.message || "erro desconhecido" : null}
          onAssociar={associarFatura}
          onClose={() => setCorrigirMovimento(null)}
        />
      )}

      {contaAEditar && (
        <ModalEditarConta
          conta={contaAEditar}
          empresa={empresa}
          onFechar={() => setContaAEditar(null)}
          // O filtro de movimentos é por banco+conta (texto), por isso muda
          // com a edição — sem isto a tabela ficava a filtrar por um número
          // que já não existe e aparecia vazia até se voltar a clicar no cartão.
          onGuardado={() => { setBanco(""); setConta(""); }}
        />
      )}

      {parAComparar && (
        <PainelCompararContas
          empresa={empresa}
          contaIdA={parAComparar.conta_id_a}
          contaIdB={parAComparar.conta_id_b}
          onFechar={() => { setParAComparar(null); setBanco(""); setConta(""); }}
        />
      )}
    </div>
  );
}
